/**
 * The permission catalogue, grouped for UI rendering (the Roles & Permissions matrix editor).
 *
 * WHY THIS IS DERIVED FROM `PERMISSIONS` RATHER THAN A SEPARATE HAND-MAINTAINED LIST: an earlier
 * version of this file was a hand-picked subset — about 40 of the ~150 permissions in
 * `permissions.ts` — because it was written once and never updated as new permissions were added.
 * The practical effect was that an admin creating a custom role could not grant most of the
 * permissions the system actually checks (most of attendance, all of chat, workflow, onboarding,
 * performance, compliance, helpdesk...). Building the catalogue BY WALKING `PERMISSIONS` means a
 * permission added there without a label falls back to an auto-generated one (`labelOf`) instead of
 * silently vanishing from the matrix — the failure mode is an ugly label, not a missing permission.
 * `catalogue.test.ts` still asserts every permission has curated metadata, so the fallback is a
 * safety net, not a licence to skip writing one.
 */

import { PERMISSIONS, type Permission } from './permissions.js';

export interface PermissionMeta {
  readonly label: string;
  readonly description: string;
}

export interface PermissionCategoryMeta {
  readonly id: string;
  readonly name: string;
  readonly description: string;
}

export interface CataloguedPermission {
  readonly key: string;
  readonly label: string;
  readonly description: string;
}

export interface PermissionCategoryEntry {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** Mutable, not `readonly`, so this structurally matches the contract's zod-inferred array type
   * without a cast at every call site that returns it over the wire. */
  readonly permissions: CataloguedPermission[];
}

// ---------------------------------------------------------------------------
// Categories, in display order. Mirrors the section comments in permissions.ts exactly, so the two
// files stay easy to read side by side.
// ---------------------------------------------------------------------------

const CATEGORIES: readonly PermissionCategoryMeta[] = [
  {
    id: 'org',
    name: 'Organisation & Settings',
    description:
      'Company profile, legal entities, locations, departments, posts and platform configuration',
  },
  {
    id: 'access_security',
    name: 'Security, Audit & Access',
    description: 'User accounts, role grants, SSO, API keys and the audit trail',
  },
  {
    id: 'people',
    name: 'People & Directory',
    description: 'Employee lifecycle, profiles, employment records, documents and compensation',
  },
  {
    id: 'attendance',
    name: 'Attendance & Shifts',
    description: 'Time tracking, punch clock, shift rostering, and regularisation approvals',
  },
  {
    id: 'leave',
    name: 'Leave Management',
    description: 'Leave requests, balance adjustments, and policy configuration',
  },
  {
    id: 'payroll',
    name: 'Payroll Operations',
    description: 'Salary processing, statutory compliance, tax runs, payslips and payouts',
  },
  {
    id: 'expense',
    name: 'Expenses & Travel',
    description: 'Expense claims, approvals, settlement, travel requests and the travel desk',
  },
  {
    id: 'chat',
    name: 'Chat & Announcements',
    description: 'In-house messaging, channels, broadcasts and legal hold',
  },
  {
    id: 'workflow',
    name: 'Approval Workflows',
    description: 'Workflow definitions, approval steps and delegation',
  },
  {
    id: 'recruitment',
    name: 'Recruitment & Hiring',
    description: 'Job requisitions, candidate pipelines, interviews, offers and referrals',
  },
  {
    id: 'onboarding',
    name: 'Onboarding & Offboarding',
    description: 'New-hire onboarding, exits, and asset tracking',
  },
  {
    id: 'assets',
    name: 'Asset Management',
    description: 'Asset register, requests, assignment and category configuration',
  },
  {
    id: 'performance',
    name: 'Performance & Learning',
    description: 'Goals, reviews, feedback and learning assignment',
  },
  {
    id: 'compliance',
    name: 'Compliance & Documents',
    description: 'Policies, POSH cases, grievances and data-subject requests',
  },
  {
    id: 'helpdesk',
    name: 'Helpdesk & Engagement',
    description: 'Support tickets, surveys and recognition',
  },
  {
    id: 'analytics',
    name: 'Reports & Analytics',
    description: 'Reporting and workforce analytics',
  },
  {
    id: 'ai',
    name: 'AI Copilot & Agent',
    description: 'AI assistant usage, agent write actions, and AI-specific audit and settings',
  },
  {
    id: 'integrations',
    name: 'Integrations',
    description: 'Third-party connections, bulk import and webhooks',
  },
  {
    id: 'app_integrations',
    name: 'Connected Applications',
    description: 'Create employees in outside applications and keep their details up to date',
  },
  {
    id: 'notifications',
    name: 'Notifications',
    description: 'Notification channel configuration and delivery activity',
  },
];

/** Maps a permission's resource prefix (before the first `.` or `:`) to a category id. */
const CATEGORY_BY_PREFIX: Record<string, string> = {
  org: 'org',
  legal_entity: 'org',
  location: 'org',
  department: 'org',
  post: 'org',

  user: 'access_security',
  role: 'access_security',
  sso: 'access_security',
  apikey: 'access_security',
  audit: 'access_security',

  employee: 'people',
  employment: 'people',
  compensation: 'people',
  org_chart: 'people',

  attendance: 'attendance',
  shift: 'attendance',
  timesheet: 'attendance',
  overtime: 'attendance',
  clock_out_policy: 'attendance',

  leave: 'leave',
  holiday_calendar: 'leave',

  payroll: 'payroll',
  payslip: 'payroll',
  bank_file: 'payroll',
  statutory: 'payroll',
  form16: 'payroll',
  loan: 'payroll',
  fnf: 'payroll',

  expense: 'expense',
  travel: 'expense',

  chat: 'chat',
  announcement: 'chat',

  workflow: 'workflow',

  requisition: 'recruitment',
  candidate: 'recruitment',
  interview: 'recruitment',
  offer: 'recruitment',
  recruitment: 'recruitment',
  referral: 'recruitment',

  onboarding: 'onboarding',
  offboarding: 'onboarding',
  asset: 'onboarding',

  goal: 'performance',
  review: 'performance',
  feedback: 'performance',
  learning: 'performance',

  policy: 'compliance',
  compliance: 'compliance',
  posh: 'compliance',
  grievance: 'compliance',
  dsr: 'compliance',

  ticket: 'helpdesk',
  survey: 'helpdesk',
  recognition: 'helpdesk',

  report: 'analytics',
  analytics: 'analytics',

  ai: 'ai',

  integration: 'integrations',
  import: 'integrations',
  webhook: 'integrations',
  app_integration: 'app_integrations',

  notification: 'notifications',
};

/**
 * Per-permission category override, applied before `CATEGORY_BY_PREFIX`. Needed here because
 * `asset:manage` (onboarding's "laptop issued" checkbox) and the new asset-register permissions
 * share the `asset` prefix but belong in different categories — a blanket prefix change would move
 * `asset:manage` out of Onboarding & Offboarding, which nothing asked for.
 */
const CATEGORY_OVERRIDE: Partial<Record<Permission, string>> = {
  'asset:read': 'assets',
  'asset:create': 'assets',
  'asset:edit': 'assets',
  'asset:delete': 'assets',
  'asset:assign': 'assets',
  'asset:transfer': 'assets',
  'asset:return': 'assets',
  'asset:retire': 'assets',
  'asset:dispose': 'assets',
  'asset.request:create': 'assets',
  'asset.request:approve': 'assets',
  'asset.category:manage': 'assets',
  'asset.financial:read': 'assets',
  'asset:financial:manage': 'assets',
  'asset:maintenance:read': 'assets',
  'asset:maintenance:create': 'assets',
  'asset:maintenance:manage': 'assets',
  'asset.maintenance:cost_input': 'assets',
  'asset:audit:read': 'assets',
  'asset:audit:create': 'assets',
  'asset:audit:verify': 'assets',
  'asset:audit:manage': 'assets',
  'asset:audit:close': 'assets',
  'asset:analytics:read': 'assets',
  'asset:analytics:export': 'assets',
  'asset.category:read': 'assets',
  'asset.type:read': 'assets',
  'asset.type:manage': 'assets',
  'asset.vendor:read': 'assets',
  'asset.vendor:manage': 'assets',
  'asset:export': 'assets',
  'asset.batch:read': 'assets',
  'asset.batch:manage': 'assets',
};

// ---------------------------------------------------------------------------
// Per-permission label + description, curated for readability in the matrix editor.
// ---------------------------------------------------------------------------

const PERMISSION_META: Partial<Record<Permission, PermissionMeta>> = {
  // Organisation & platform
  'org:read': {
    label: 'View Organisation',
    description: "Read the organisation's profile and structure",
  },
  'org:update': {
    label: 'Manage Organisation',
    description: 'Edit organisation profile and branding',
  },
  'org.settings:read': {
    label: 'View Organisation Settings',
    description: 'Read tenant-wide configuration',
  },
  'org.settings:update': {
    label: 'Manage Organisation Settings',
    description: 'Change tenant-wide configuration',
  },
  'org.branding:update': {
    label: 'Manage Branding',
    description: 'Update logo, colours and public-facing branding',
  },
  'org.billing:read': {
    label: 'View Billing',
    description: 'See the subscription plan and invoices',
  },
  'org.billing:update': {
    label: 'Manage Billing',
    description: 'Change subscription plan and payment details',
  },
  'org.company:create': {
    label: 'Create Another Company',
    description:
      'Create a new company of the group from inside the app, with its own web address, and switch to it',
  },
  'org.group:manage': {
    label: 'Manage Group Access',
    description:
      "Give people access to the group's companies, change or remove it, and send new sign-in links (group owners only)",
  },
  'agreement:read': {
    label: 'View Agreement',
    description: "See the org's MSA/DPA signing status and history",
  },
  'agreement:manage': {
    label: 'Manage Agreement',
    description: 'Create and send the MSA/DPA for signature',
  },
  'legal_entity:read': {
    label: 'View Legal Entities',
    description: 'Read registered legal entities',
  },
  'legal_entity:create': {
    label: 'Create Legal Entity',
    description: 'Register a new legal entity',
  },
  'legal_entity:update': {
    label: 'Update Legal Entity',
    description: "Edit a legal entity's details",
  },
  'location:read': { label: 'View Locations', description: 'Read branch and location records' },
  'location:manage': {
    label: 'Manage Locations',
    description: 'Create, edit and close branch locations',
  },
  'department:read': { label: 'View Departments', description: 'Read department records' },
  'department:manage': {
    label: 'Manage Departments',
    description: 'Create, edit and close departments',
  },
  'post:read': { label: 'View Posts', description: 'Read job posts / position catalogue' },
  'post:manage': { label: 'Manage Posts', description: 'Create and edit job posts / positions' },

  // Identity & access
  'user:read': { label: 'View User Accounts', description: 'See login accounts and their status' },
  'user:create': {
    label: 'Create User Account',
    description: 'Provision a login account for a person',
  },
  'user:update': { label: 'Update User Account', description: 'Edit a login account' },
  'user:deactivate': { label: 'Deactivate User Account', description: 'Disable a login account' },
  'salary_pin:reset': {
    label: 'Reset Salary PIN',
    description: "Clear a person's salary PIN so they create a new one; never reveals or sets it",
  },
  'user.impersonate:execute': {
    label: 'Impersonate User',
    description: 'Log in as another user for support, fully audited',
  },
  'role:read': {
    label: 'View Roles & Grants',
    description: 'See role definitions and who holds them',
  },
  'role:assign': {
    label: 'Manage Roles & Grants',
    description: 'Create custom roles and assign roles to people',
  },
  'sso:manage': { label: 'Manage SSO', description: 'Configure single sign-on identity providers' },
  'apikey:manage': { label: 'Manage API Keys', description: 'Create and revoke API keys' },
  'audit:read': { label: 'View Audit Log', description: 'Read the system audit trail' },
  'audit:export': { label: 'Export Audit Log', description: 'Export the audit trail' },

  // People
  'employee:read': { label: 'View Employees', description: 'Access the employee directory' },
  'employee:create': { label: 'Add Employee', description: 'Create new employee records' },
  'employee:update': { label: 'Update Employee', description: 'Edit employee profiles' },
  'employee:delete': { label: 'Deactivate Employee', description: 'Deactivate an employee record' },
  'employee:export': { label: 'Export Employees', description: 'Export the employee directory' },
  'employee.pii:read': {
    label: 'View Sensitive PII',
    description: 'See Aadhaar, PAN, bank account and date of birth',
  },
  'employee.pii:export': {
    label: 'Export Sensitive PII',
    description: 'Download full Aadhaar, PAN and bank account numbers in the employee export',
  },
  'employee.directory:read': {
    label: 'View Employee Directory',
    description: 'Find colleagues and see their work contact details, team and manager',
  },
  'employee.document:read': {
    label: 'View Employee Documents',
    description: 'See uploaded employee documents',
  },
  'employee.document:manage': {
    label: 'Manage Employee Documents',
    description: 'Upload and remove employee documents',
  },
  'employment:read': {
    label: 'View Employment Records',
    description: 'See job, grade and assignment history',
  },
  'employment:create': {
    label: 'Create Employment',
    description: 'Create a new employment record',
  },
  'employment:update': { label: 'Update Employment', description: 'Edit an employment record' },
  'employment.transfer:execute': {
    label: 'Transfer Employee',
    description: 'Move an employee between entities or locations',
  },
  'employment.exit:execute': { label: 'Process Exit', description: "Initiate an employee's exit" },
  'compensation:read': {
    label: "View other people's salary",
    description: "See the salary and pay components of the people in this role's scope",
  },
  'compensation.own:read': {
    label: 'View own salary',
    description: "See one's own salary and pay components",
  },
  'compensation:update': {
    label: 'Update Compensation',
    description: 'Change salary and pay components',
  },
  'compensation.cycle:manage': {
    label: 'Manage Compensation Cycles',
    description: 'Run appraisal / compensation revision cycles',
  },
  'org_chart:read': { label: 'View Org Chart', description: 'See the reporting structure' },
  'org_chart:update': { label: 'Update Org Chart', description: 'Change reporting lines' },

  // Attendance & time
  'attendance:read': {
    label: 'View Own Attendance',
    description: 'View your own attendance records',
  },
  'attendance.team:read': {
    label: "View Others' Attendance",
    description:
      'View attendance of the people within the grant scope (team, department, entity or org)',
  },
  'attendance.policy:manage': {
    label: 'Manage Attendance Rules',
    description:
      'Configure regularisation rules, late-mark penalty and overtime rules (Attendance Command Center)',
  },
  'attendance:punch': { label: 'Clock In / Out', description: 'Execute clock punches' },
  'attendance:update': { label: 'Update Attendance', description: 'Correct attendance records' },
  'attendance:approve': { label: 'Approve Attendance', description: 'Approve attendance logs' },
  'attendance.regularisation:request': {
    label: 'Request Regularisation',
    description: 'File an attendance regularisation request',
  },
  'attendance.regularisation:approve': {
    label: 'Approve Regularisation',
    description: 'Approve attendance punch overrides',
  },
  'attendance.cycle:freeze': {
    label: 'Freeze Attendance Cycle',
    description: "Lock a period's attendance ahead of payroll",
  },
  'shift:read': { label: 'View Shifts', description: 'View shift definitions and assignments' },
  'shift:manage': { label: 'Manage Shifts', description: 'Create and assign shifts' },
  'shift.roster:manage': {
    label: 'Manage Shift Roster',
    description: 'Build and publish the shift roster',
  },
  'shift.rotation:manage': {
    label: 'Manage Shift Groups & Rotation',
    description: 'Create shift groups and assign employees to a standing rotation',
  },
  'shift.change:request': {
    label: 'Request Shift Change',
    description: 'Request a change to an assigned shift',
  },
  'shift.change:approve': {
    label: 'Approve Shift Change',
    description: 'Approve a shift change request',
  },
  'attendance.od:request': {
    label: 'Request On-Duty / Remote',
    description: 'Request on-duty or remote-work marking',
  },
  'attendance.od:approve': {
    label: 'Approve On-Duty / Remote',
    description: 'Approve on-duty or remote-work requests',
  },
  'attendance.penalty:manage': {
    label: 'Manage Early-Exit Policies',
    description: 'Configure early-exit grace/warning/penalty overrides and payroll caps',
  },
  'attendance.early_exit_waiver:request': {
    label: 'Request Early-Exit Waiver',
    description: 'Request a waiver of an early-exit penalty',
  },
  'attendance.early_exit_waiver:approve': {
    label: 'Approve Early-Exit Waiver',
    description: 'Approve or reject an early-exit waiver request',
  },
  'clock_out_policy:manage': {
    label: 'Manage Clock-Out Policy',
    description:
      'Configure which conditions (EOD report, overdue tasks, mandatory learning) block clock-out',
  },
  'biometric_device:manage': {
    label: 'Manage Biometric Devices',
    description: 'Register, edit and deactivate physical biometric attendance devices',
  },
  'timesheet:read': { label: 'View Timesheets', description: 'View submitted timesheets' },
  'timesheet:submit': { label: 'Submit Timesheet', description: 'Submit a timesheet' },
  'timesheet:approve': { label: 'Approve Timesheet', description: 'Approve submitted timesheets' },
  'overtime:approve': { label: 'Approve Overtime', description: 'Approve overtime hours' },

  // Leave
  'leave:read': { label: 'View Leave', description: 'Access the leave calendar and requests' },
  'leave:request': { label: 'Submit Leave Request', description: 'File leave applications' },
  'leave:approve': { label: 'Approve Leave', description: 'Approve or reject leave requests' },
  'leave:cancel': { label: 'Cancel Leave', description: 'Cancel a leave request' },
  'leave.balance:read': {
    label: 'View Leave Balances',
    description: 'See leave balance allocations',
  },
  'leave.balance:adjust': {
    label: 'Adjust Leave Balances',
    description: 'Modify leave balance allocations',
  },
  'leave.policy:read': {
    label: 'View Leave Policies',
    description: 'Read leave types and policies',
  },
  'leave.policy:manage': {
    label: 'Manage Leave Policies',
    description: 'Configure leave types and policies',
  },
  'leave.encashment:request': {
    label: 'Request Leave Encashment',
    description: 'Ask to be paid for unused leave days',
  },
  'leave.comp_off:request': {
    label: 'Request Comp-Off',
    description: 'Ask to be credited a day worked on a holiday or weekly off',
  },
  'leave.cancel:on_behalf': {
    label: 'Cancel Leave for Others',
    description: 'Cancel leave for someone else, including leave that has already started',
  },
  'leave.year_end:read': {
    label: 'View Leave Year-End',
    description: 'See year-end runs and every encashment request',
  },
  'leave.year_end:execute': {
    label: 'Run Leave Year-End',
    description:
      'Draft, apply or discard the carry-forward, encashment and lapse of leave balances',
  },
  'leave.report:read': {
    label: 'View Leave Reports',
    description: 'Run the leave register, lapse, trends and loss-of-pay reports',
  },
  'leave.plan:read': {
    label: 'View Leave Plans',
    description: 'See leave plans, who they apply to, and which rules apply to a person',
  },
  'leave.plan:manage': {
    label: 'Manage Leave Plans',
    description: 'Create leave plans, set their rules from a date and aim them at people',
  },
  'holiday_calendar:manage': {
    label: 'Manage Holiday Calendar',
    description: 'Configure the holiday calendar',
  },

  // Payroll
  'payroll:read': { label: 'View Payroll', description: 'Access payroll summaries and runs' },
  'payroll.run:create': { label: 'Create Payroll Run', description: 'Start a new payroll run' },
  'payroll.run:execute': {
    label: 'Execute Payroll Run',
    description: 'Run the statutory engine for a payroll run',
  },
  'payroll.run:approve': {
    label: 'Approve Payroll Run',
    description: 'Authorise a payroll run for disbursement',
  },
  'payroll.run:finalize': {
    label: 'Finalize Payroll Run',
    description: 'Lock an approved payroll run as the final payroll result',
  },
  'payroll.run:release': {
    label: 'Release Payroll Run',
    description: 'Release a finalized payroll run for payslip publication and payment',
  },
  'payroll.run:reopen': {
    label: 'Reopen Payroll Run',
    description: 'Unlock a completed payroll run',
  },
  'payroll.input:manage': {
    label: 'Manage Payroll Inputs',
    description: 'Enter and adjust payroll inputs',
  },
  'payroll.rules:read': {
    label: 'View Payroll Rules',
    description: 'Read statutory / pay component rules',
  },
  'payroll.rules:manage': {
    label: 'Manage Payroll Rules',
    description: 'Configure statutory / pay component rules',
  },
  'payroll.rules:publish': {
    label: 'Publish Payroll Rules',
    description:
      'Validate, publish and archive rule versions and entity rule configuration (never one you authored)',
  },
  'payroll.rules:rollout': {
    label: 'Roll Out the Payroll Engine',
    description:
      'Switch a legal entity onto the rule engine (engine mode), which changes how pay is calculated',
  },
  'payroll.rules.override:manage': {
    label: 'Manage Payroll Rule Overrides',
    description: 'Create and end legal-entity and employment overrides of rule parameters',
  },
  'payslip:read': { label: 'View Payslips', description: "Access any employee's payslip" },
  'payslip.own:read': { label: 'View Own Payslip', description: 'Access their own payslip' },
  'payslip:publish': {
    label: 'Publish Payslips',
    description: 'Publish payslips for a payroll run',
  },
  'bank_file:generate': {
    label: 'Generate Bank File',
    description: 'Generate the disbursement bank file',
  },
  'statutory.filing:manage': {
    label: 'Manage Statutory Filings',
    description: 'File statutory returns (PF, ESI, TDS, etc.)',
  },
  'form16:generate': { label: 'Generate Form 16', description: 'Generate annual tax certificates' },
  'loan:manage': {
    label: 'Manage Employee Loans',
    description: 'Administer employee loans and recoveries',
  },
  'fnf:execute': {
    label: 'Execute Full & Final Settlement',
    description: "Create and settle an employee's final settlement",
  },
  'fnf:read': {
    label: 'View Full & Final Settlements',
    description: 'See settlements, their calculation versions and clearances',
  },
  'fnf:approve': {
    label: 'Approve Full & Final Settlement',
    description: 'Approve the exact calculation version of a settlement (never your own)',
  },
  'fnf.clearance:manage': {
    label: 'Manage Exit Clearances',
    description: 'Define exit clearance departments, owners and what each one gates',
  },
  'payroll.payment:read': {
    label: 'View Payments',
    description: 'See payment batches, obligations and attempts with masked bank details',
  },
  'payroll.payment:prepare': {
    label: 'Prepare Payment Batches',
    description: 'Assemble a payment batch and submit it for authorisation',
  },
  'payroll.payment:authorise': {
    label: 'Authorise Payment Batches',
    description: 'Authorise the exact contents of a payment batch (never one you prepared)',
  },
  'payroll.payment:transmit': {
    label: 'Transmit Payments',
    description: 'Export bank files, transmit authorised batches and retry definitive failures',
  },
  'payroll.payment:reconcile': {
    label: 'Reconcile Payments',
    description: 'Record payment outcomes with evidence and resolve unknown outcomes',
  },
  'payroll.payment.team:read': {
    label: 'View Team Payment Status',
    description: 'See whether your reports were paid, without amounts or bank details',
  },
  'payroll.payment.own:read': {
    label: 'View Own Payment Status',
    description: 'See the status of your own salary payments',
  },
  'payroll.hold:request': {
    label: 'Request Salary Hold',
    description: "Ask for a team member's salary to be held",
  },
  'payroll.hold:manage': {
    label: 'Manage Salary Holds',
    description: 'Prepare hold requests for approval and withdraw them',
  },
  'payroll.hold:approve': {
    label: 'Approve Salary Holds',
    description: 'Put a salary hold into effect and release it (never your own)',
  },
  'payroll.offcycle:manage': {
    label: 'Manage Off-cycle Payments',
    description: 'Create, calculate and submit off-cycle payments',
  },
  'payroll.offcycle:approve': {
    label: 'Approve Off-cycle Payments',
    description: 'Approve an off-cycle payment (never your own)',
  },
  'payslip.policy:manage': {
    label: 'Manage Payslip Policy',
    description: 'Decide when payslips are published relative to payment',
  },

  // Expenses & travel
  'expense:read': { label: 'View Expenses', description: 'Access expense claims' },
  'expense:submit': { label: 'Submit Expense Claim', description: 'File an expense claim' },
  'expense:approve': {
    label: 'Approve Expense Claim',
    description: 'Approve or reject expense claims',
  },
  'expense:settle': {
    label: 'Settle Expense Claim',
    description: 'Mark an approved claim as paid',
  },
  'expense.policy:manage': {
    label: 'Manage Expense Policy',
    description: 'Configure expense categories and limits',
  },
  'expense.policy:read': {
    label: 'View Expense Policy',
    description: 'See expense categories and limits, including inactive ones',
  },
  'travel:request': {
    label: 'Request Travel',
    description: 'Request trips for oneself and answer trip invitations',
  },
  'travel:approve': {
    label: 'Approve Travel',
    description: "View someone else's travel requests and record travel desk bookings",
  },
  'travel:read': {
    label: 'View All Trips',
    description: "See everyone's trips, file a trip on someone's behalf, see who is travelling",
  },
  'travel.desk:read': {
    label: 'View Travel Desk Queues',
    description: "See the booking queues and history of one's travel desk teams",
  },
  'travel.desk:book': {
    label: 'Book Travel',
    description: "Record options, bookings, cancellations and refunds for one's desk teams",
  },
  'travel.desk:manage': {
    label: 'Manage Travel Desk',
    description: 'Desk teams, members, coverage, vendors, reassignment and every queue',
  },
  'travel.policy:manage': {
    label: 'Manage Travel Policy',
    description: 'Travel policies, entitlements, city tiers, per-diem rules and the module switch',
  },

  // Chat
  'chat:read': { label: 'View Chat', description: 'Read chat conversations' },
  'chat:send': { label: 'Send Messages', description: 'Send chat messages' },
  'chat.channel:create': { label: 'Create Channel', description: 'Create a new chat channel' },
  'chat.channel:manage': {
    label: 'Manage Channels',
    description: 'Administer chat channels and membership',
  },
  'chat.message:delete_any': {
    label: 'Delete Any Message',
    description: "Delete another person's message",
  },
  'chat.announcement:broadcast': {
    label: 'Broadcast Announcement',
    description: 'Send an org-wide announcement',
  },
  'chat.export:execute': { label: 'Export Chat', description: 'Export chat history' },
  'chat.import:execute': {
    label: 'Import Chat History',
    description: 'Bulk-import historical chat messages from another platform',
  },
  'chat.legal_hold:manage': {
    label: 'Manage Legal Hold',
    description: 'Place or release a legal hold on chat data',
  },
  'chat.dlp:review': {
    label: 'Review Flagged Messages',
    description: 'Review chat messages the DLP scanner flagged and mark them reviewed',
  },
  'chat.settings:manage': {
    label: 'Manage Chat Settings',
    description:
      'Configure chat for the organisation: message edit window, link previews, presence and archived-channel visibility',
  },

  // Announcements (Engage)
  'announcement:read': {
    label: 'View Announcements',
    description: 'Read the announcements that apply to you, react and acknowledge them',
  },
  'announcement:manage': {
    label: 'Manage Announcements',
    description: 'Write, schedule, pin and archive announcements, and see who has read them',
  },

  // Workflow
  'workflow:read': {
    label: 'View Workflows',
    description: 'See approval workflow definitions and instances',
  },
  'workflow:manage': {
    label: 'Manage Workflow Definitions',
    description: 'Create and edit approval workflow definitions',
  },
  'workflow.instance:approve': {
    label: 'Approve Workflow Step',
    description: 'Act on an approval step assigned to them',
  },
  'workflow.delegate:manage': {
    label: 'Manage Approval Delegation',
    description: 'Delegate their approvals to someone else',
  },

  // Recruitment
  'requisition:read': { label: 'View Requisitions', description: 'Read job requisitions' },
  'requisition:create': { label: 'Create Requisition', description: 'Open a new job requisition' },
  'requisition:update': { label: 'Update Requisition', description: 'Edit a job requisition' },
  'requisition:close': { label: 'Close Requisition', description: 'Close a job requisition' },
  'requisition:approve': {
    label: 'Approve Requisition',
    description: 'Approve a new job requisition',
  },
  'candidate:read': { label: 'View Candidates', description: 'Access the applicant pipeline' },
  'candidate:manage': {
    label: 'Manage Candidates',
    description: 'Move candidates through the pipeline',
  },
  'interview:read': { label: 'View Interviews', description: 'See scheduled interviews' },
  'interview:schedule': {
    label: 'Schedule Interview',
    description: 'Schedule a candidate interview',
  },
  'interview.feedback:submit': {
    label: 'Submit Interview Feedback',
    description: 'Submit feedback for an interview round',
  },
  'offer:create': { label: 'Create Offer', description: 'Generate an offer letter' },
  'offer:send': { label: 'Send Offer', description: 'Send an offer letter to a candidate' },
  'offer:approve': { label: 'Approve Offer', description: 'Authorise an offer letter' },
  'offer:rescind': { label: 'Rescind Offer', description: 'Withdraw a sent offer' },
  'offer.compensation:read': {
    label: 'View Offer Salaries',
    description: 'See the CTC on offers. Without it an offer is listed with its salary hidden',
  },
  'recruitment.analytics:read': {
    label: 'View Recruitment Analytics',
    description: 'See hiring funnel and cost-per-hire analytics',
  },
  'referral:read': { label: 'View Referrals', description: 'See employee referrals' },
  'referral:create': { label: 'Submit Referral', description: 'Refer a candidate' },

  // Onboarding / offboarding
  'onboarding:read': {
    label: 'View Onboarding',
    description: 'See onboarding processes and checklists',
  },
  'onboarding:manage': {
    label: 'Manage Onboarding',
    description: 'Configure and run onboarding processes',
  },
  'offboarding:read': { label: 'View Offboarding', description: 'See offboarding processes' },
  'offboarding:manage': {
    label: 'Manage Offboarding',
    description: 'Configure and run offboarding processes',
  },
  'asset:manage': { label: 'Manage Assets', description: 'Track and assign company assets' },

  // Asset management
  'asset:read': {
    label: 'View Assets',
    description: 'See the asset register, assignments and status',
  },
  'asset:create': { label: 'Add Asset', description: 'Add a new asset to the register' },
  'asset:edit': { label: 'Edit Asset', description: "Edit an asset's record" },
  'asset:delete': {
    label: 'Delete Asset',
    description: 'Delete draft or unallocated asset from register',
  },
  'asset:assign': { label: 'Assign Asset', description: 'Assign an asset to a person' },
  'asset:transfer': {
    label: 'Transfer Asset',
    description: 'Transfer an assigned asset between people or locations',
  },
  'asset:return': {
    label: 'Return Asset',
    description: 'Check in and process return of an assigned asset',
  },
  'asset:retire': { label: 'Retire Asset', description: 'Retire an asset from active service' },
  'asset:dispose': {
    label: 'Dispose Asset',
    description: 'Dispose of a retired asset (sale, scrap, donate)',
  },
  'asset.request:create': {
    label: 'Request Asset',
    description: 'Request an asset for themselves',
  },
  'asset.request:approve': {
    label: 'Approve Asset Request',
    description: 'Approve or reject an asset request',
  },
  'asset.category:manage': {
    label: 'Manage Asset Categories',
    description: 'Configure the asset category taxonomy',
  },
  'asset.financial:read': {
    label: 'View Asset Financials',
    description: 'See asset cost and depreciated value',
  },
  'asset:financial:manage': {
    label: 'Manage Asset Financials',
    description: 'Configure valuation, depreciation and capital settings',
  },
  'asset:maintenance:read': {
    label: 'View Maintenance',
    description: 'See maintenance requests, diagnostics and work orders',
  },
  'asset:maintenance:create': {
    label: 'Report Hardware Issue',
    description: 'Log a new maintenance request or issue',
  },
  'asset:maintenance:manage': {
    label: 'Manage Maintenance',
    description: 'Update issue diagnostics, triage and technician assignments',
  },
  'asset.maintenance:cost_input': {
    label: 'Record Repair Cost',
    description: 'Enter vendor repair invoice amounts on work orders',
  },
  'asset:audit:read': {
    label: 'View Asset Audits',
    description: 'See physical audit schedules and discrepancy reports',
  },
  'asset:audit:create': {
    label: 'Schedule Asset Audit',
    description: 'Create and launch new physical asset audits',
  },
  'asset:audit:verify': {
    label: 'Reconcile Audit Scans',
    description: 'Reconcile physical audit scans against asset records',
  },
  'asset:audit:manage': {
    label: 'Manage Asset Audits',
    description: 'Edit, reassign auditors on, or cancel a physical audit in progress',
  },
  'asset:audit:close': {
    label: 'Close Asset Audits',
    description: 'Close a physical audit with its summary and produce the discrepancy report',
  },
  'asset:analytics:read': {
    label: 'View Asset Analytics',
    description: 'See asset lifecycle, utilization and depreciation metrics',
  },
  'asset:analytics:export': {
    label: 'Export Asset Analytics',
    description: 'Download asset lifecycle and valuation analytics data',
  },
  'asset.category:read': {
    label: 'View Asset Categories',
    description: 'Read the asset category taxonomy',
  },
  'asset.type:read': { label: 'View Asset Types', description: 'Read the asset type catalog' },
  'asset.type:manage': {
    label: 'Manage Asset Types',
    description: 'Create and edit asset types within a category',
  },
  'asset.vendor:read': { label: 'View Vendors', description: 'Read the asset vendor directory' },
  'asset.vendor:manage': { label: 'Manage Vendors', description: 'Create and edit vendor records' },
  'asset:export': {
    label: 'Export Assets',
    description: 'Export asset configuration, vendors and inventory to CSV',
  },
  'asset.batch:read': { label: 'View Asset Batches', description: 'Read procurement batches' },
  'asset.batch:manage': {
    label: 'Manage Asset Batches',
    description: 'Create and edit procurement batches and bulk imports',
  },

  // Performance & learning
  'goal:read': { label: 'View Goals', description: 'See goals and objectives' },
  'goal:manage': { label: 'Manage Goals', description: "Set and edit a team's goals" },
  'review:read': {
    label: 'View Performance Reviews',
    description: 'Read performance review records',
  },
  'review:submit': {
    label: 'Submit Performance Review',
    description: 'Submit a performance review',
  },
  'review.cycle:manage': {
    label: 'Manage Review Cycles',
    description: 'Configure and run review cycles',
  },
  'review.calibrate:execute': {
    label: 'Calibrate Reviews',
    description: 'Run calibration across a review cycle',
  },
  'feedback:give': { label: 'Give Feedback', description: 'Give peer or upward feedback' },
  'learning:read': { label: 'View Learning', description: 'See learning courses and assignments' },
  'learning:manage': {
    label: 'Manage Learning Content',
    description: 'Create and edit learning courses',
  },
  'learning.assign:execute': {
    label: 'Assign Learning',
    description: 'Assign learning content to employees',
  },
  'learning.override:execute': {
    label: 'Override Training Gate',
    description:
      "Release a trainee's clock-out block, waive a webcam requirement, or revoke a certificate",
  },
  'learning.recording:view': {
    label: 'View Sensitive Training Recordings',
    description: 'View webcam footage captured during a training task. Every view is access-logged',
  },
  'learning.group:manage': {
    label: 'Run Training Across Group Companies',
    description:
      "See every group company's courses and live sessions in one place, copy and assign courses to several companies, and reschedule or cancel their sessions (group owners only)",
  },

  // Compliance & documents
  'policy:read': { label: 'View Policies', description: 'Read company policies' },
  'policy:manage': { label: 'Manage Policies', description: 'Publish and update company policies' },
  'policy:acknowledge': {
    label: 'Acknowledge Policy',
    description: 'Acknowledge a published policy',
  },
  'compliance:read': {
    label: 'View Compliance',
    description: 'See the statutory compliance calendar and status',
  },
  'compliance:manage': {
    label: 'Manage Compliance',
    description: 'Manage compliance tasks and filings',
  },
  'posh.case:read': { label: 'View POSH Cases', description: 'See POSH case records' },
  'posh.case:manage': { label: 'Manage POSH Cases', description: 'Manage POSH case handling' },
  'grievance:raise': { label: 'Raise Grievance', description: 'File a grievance' },
  'grievance:manage': { label: 'Manage Grievances', description: 'Handle and resolve grievances' },
  'dsr:manage': {
    label: 'Manage Data Subject Requests',
    description: 'Handle data access / erasure requests',
  },

  // Helpdesk & engagement
  'ticket:read': { label: 'View Tickets', description: 'See the helpdesk tickets one raised' },
  'ticket:create': { label: 'Create Ticket', description: 'Raise a helpdesk ticket' },
  'ticket:manage': {
    label: 'Work Tickets',
    description: "Triage and resolve tickets in the categories of one's helpdesk teams",
  },
  'ticket.all:manage': {
    label: 'Manage All Tickets',
    description: 'See and work every helpdesk ticket, confidential categories included',
  },
  'ticket.config:manage': {
    label: 'Configure Helpdesk',
    description: 'Manage helpdesk categories, teams, SLA targets and business hours',
  },
  'ticket.report:read': {
    label: 'View Helpdesk Reports',
    description: 'Ticket volume, SLA compliance, response times and satisfaction',
  },
  'survey:read': { label: 'View Surveys', description: 'See survey results' },
  'survey:respond': { label: 'Respond to Survey', description: 'Submit a survey response' },
  'survey:manage': { label: 'Manage Surveys', description: 'Create and publish surveys' },
  'recognition:give': { label: 'Give Recognition', description: 'Recognise a colleague' },
  'recognition:manage': {
    label: 'Manage Recognition',
    description: 'Administer the recognition programme',
  },

  // Analytics
  'report:read': { label: 'View Reports', description: 'Access saved reports' },
  'report:create': { label: 'Create Report', description: 'Build a new report' },
  'report:export': { label: 'Export Report', description: 'Export report data' },
  'analytics.compensation:read': {
    label: 'View Compensation Analytics',
    description: 'See pay-band and compensation analytics',
  },
  'analytics.attrition:read': {
    label: 'View Attrition Analytics',
    description: 'See attrition and retention analytics',
  },

  // AI
  'ai.copilot:use': { label: 'Use AI Copilot', description: 'Use the in-app AI assistant' },
  'ai.agent.write:execute': {
    label: 'Let AI Agent Take Actions',
    description: "Allow the AI agent to propose write actions on this person's behalf",
  },
  'ai.audit:read': {
    label: 'View AI Audit Log',
    description: 'See a record of AI-initiated actions',
  },
  'ai.settings:manage': {
    label: 'Manage AI Settings',
    description: 'Configure AI provider and capability settings',
  },

  // Integrations
  'integration:read': {
    label: 'View Integrations',
    description: 'See connected third-party integrations',
  },
  'integration:manage': {
    label: 'Manage Integrations',
    description: 'Connect and configure integrations',
  },
  'import:execute': { label: 'Run Bulk Import', description: 'Run a bulk data import' },
  'webhook:manage': { label: 'Manage Webhooks', description: 'Configure outbound webhooks' },

  // Connected applications
  'app_integration:read': {
    label: 'View Connected Applications',
    description: 'See connected applications, their history and the status of each employee',
  },
  'app_integration:manage': {
    label: 'Manage Connected Applications',
    description:
      'Connect applications, send employee data to them, go live, and run bulk sends and links',
  },
  'app_integration:sync': {
    label: 'Sync Employees to Applications',
    description: 'Send, retry, link and answer questions for one employee in their scope',
  },

  // Notifications
  'notification.config:read': {
    label: 'View Notification Settings',
    description: 'See which modules and channels are enabled org-wide',
  },
  'notification.config:manage': {
    label: 'Manage Notification Settings',
    description: 'Turn notification channels on or off per module',
  },
  'notification.delivery:read': {
    label: 'View Delivery Activity',
    description: 'See the notification delivery log and failure reasons',
  },
};

/** Mechanical fallback for a permission missing from `PERMISSION_META` — see this file's header. */
function fallbackMeta(permission: Permission): PermissionMeta {
  const [resource, action] = permission.split(':');
  const words = (resource ?? permission)
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return {
    label: `${(action ?? '').replace(/\b\w/g, (c) => c.toUpperCase())} ${words}`.trim(),
    description: `${action ?? 'Access'} on ${resource ?? permission}`,
  };
}

function categoryIdFor(permission: Permission): string {
  const override = CATEGORY_OVERRIDE[permission];
  if (override) return override;
  const resource = permission.split(':')[0]!.split('.')[0]!;
  return CATEGORY_BY_PREFIX[resource] ?? 'org';
}

/**
 * The full permission catalogue, grouped for the Roles & Permissions matrix editor. Every entry in
 * `PERMISSIONS` appears exactly once — see catalogue.test.ts.
 */
export const PERMISSION_CATEGORIES: PermissionCategoryEntry[] = CATEGORIES.map((category) => ({
  ...category,
  permissions: PERMISSIONS.filter((permission) => categoryIdFor(permission) === category.id).map(
    (key) => {
      const meta = PERMISSION_META[key] ?? fallbackMeta(key);
      return { key, label: meta.label, description: meta.description };
    },
  ),
})).filter((category) => category.permissions.length > 0);
